import { useMemo } from "react";
import {
  useAnalysisData,
  useIssuesData,
  useAnalysisPctData,
  useIssuesPctData,
  useUserData,
  useSessionData,
  useVideoData,
  useScreenRecordingsData,
} from "../utils/summaryQueries";
import {
  CHART_TYPES,
  createBaseChartOptions,
  CHART_TITLES,
  processUsersData,
  processSessionsData,
  processVideosData,
  processIssuesData,
  processAnalysisData,
  processIssuesPctData,
  processAnalysisPctData,
  processScreenRecordingsData,
  processSubscriptionsData,
  processRecurringSubscriptionsData,
  processCanceledSubscriptionsData,
} from "../utils/chartConfigs";

// Custom hook for managing chart data and configuration
export const useChartData = (
  statType,
  startDate,
  endDate,
  statPeriod,
  selectionRange,
  props = {}
) => {
  // Use all the query hooks
  const analysisQuery = useAnalysisData(startDate, endDate, statPeriod);
  const issuesQuery = useIssuesData(startDate, endDate, statPeriod);
  const analysisPctQuery = useAnalysisPctData(startDate, endDate, statPeriod);
  const issuesPctQuery = useIssuesPctData(startDate, endDate, statPeriod);
  const userQuery = useUserData(startDate, endDate, statPeriod);
  const sessionQuery = useSessionData(startDate, endDate, statPeriod);
  const videoQuery = useVideoData(startDate, endDate, statPeriod);
  const screenRecordingsQuery = useScreenRecordingsData(
    startDate,
    endDate,
    statPeriod
  );

  // Determine loading state
  const isLoading = [
    analysisQuery,
    issuesQuery,
    analysisPctQuery,
    issuesPctQuery,
    userQuery,
    sessionQuery,
    videoQuery,
    screenRecordingsQuery,
  ].some((query) => query.isLoading);

  // Determine error state
  const isError = [
    analysisQuery,
    issuesQuery,
    analysisPctQuery,
    issuesPctQuery,
    userQuery,
    sessionQuery,
    videoQuery,
    screenRecordingsQuery,
  ].some((query) => query.isError);

  const error = [
    analysisQuery,
    issuesQuery,
    analysisPctQuery,
    issuesPctQuery,
    userQuery,
    sessionQuery,
    videoQuery,
    screenRecordingsQuery,
  ].find((query) => query.error)?.error;

  // Process chart data based on statType
  const { data, options } = useMemo(() => {
    if (isLoading) {
      return {
        data: { labels: [], datasets: [] },

        options: createBaseChartOptions("Recurring Subscriptions"),
      };
    }

    let chartData = { labels: [], datasets: [] };
    const title = CHART_TITLES[statType] || statType;

    switch (statType) {
      case CHART_TYPES.USERS:
        chartData = processUsersData(userQuery.data);
        break;
      case CHART_TYPES.SESSIONS:
        chartData = processSessionsData(sessionQuery.data);
        break;
      case CHART_TYPES.VIDEOS:
        chartData = processVideosData(videoQuery.data);
        break;
      case CHART_TYPES.ISSUES:
        chartData = processIssuesData(issuesQuery.data, analysisQuery.data);
        break;
      case CHART_TYPES.ANALYSIS:
        chartData = processAnalysisData(analysisQuery.data, issuesQuery.data);
        break;
      case CHART_TYPES.ISSUES_BY_PERCENTAGE:
        chartData = processIssuesPctData(
          issuesPctQuery.data,
          analysisPctQuery.data
        );
        break;
      case CHART_TYPES.ANALYSIS_BY_PERCENTAGE:
        chartData = processAnalysisPctData(
          analysisPctQuery.data,
          issuesPctQuery.data
        );
        break;
      case CHART_TYPES.SCREEN_RECORDINGS:
        chartData = processScreenRecordingsData(screenRecordingsQuery.data);
        break;
      case "subscriptions":
        chartData = processSubscriptionsData(
          props.subscriptions || [],
          selectionRange,
          statPeriod
        );
        break;
      case "recurringSubscriptions":
        chartData = processRecurringSubscriptionsData(
          props.recurringSubscriptions || props.subscriptions || [],
          selectionRange,
          statPeriod
        );
        break;
      case "canceledSubscriptions":
        chartData = processCanceledSubscriptionsData(
          props.canceledSubscriptions || props.subscriptions || [],
          selectionRange,
          statPeriod
        );
        break;
      case "confidenceScore":
      case "confidenceScoreByPercentage":
        // TODO: Implement confidence score processing
        chartData = { labels: [], datasets: [] };
        break;
      default:
        break;
    }

    return {
      data: chartData,
      options: createBaseChartOptions(title),
    };
  }, [
    statType,
    isLoading,
    userQuery.data,
    sessionQuery.data,
    videoQuery.data,
    issuesQuery.data,
    analysisQuery.data,
    issuesPctQuery.data,
    analysisPctQuery.data,
    screenRecordingsQuery.data,
    selectionRange,
    statPeriod,
    props.subscriptions,
    props.recurringSubscriptions,
    props.canceledSubscriptions,
  ]);

  return {
    data,
    options,
    isLoading,
    isError,
    error,
  };
};
